import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'accent' | 'outline';
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  tabIndex?: number;
}

export const Button = ({ variant = 'primary', children, className = '', ...props }: ButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bgRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const button = buttonRef.current;
    const bg = bgRef.current;
    if (!button || !bg) return;

    const ctx = gsap.context(() => {
      let rect: DOMRect | null = null;
      
      button.addEventListener('mouseenter', () => {
        rect = button.getBoundingClientRect();
        gsap.to(bg, {
          y: '0%',
          duration: 0.4,
          ease: 'power3.out'
        });
      });

      // Magnetic effect
      button.addEventListener('mousemove', (e) => {
        if (!rect) rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        gsap.to(button, {
          x: x * 0.2,
          y: y * 0.2,
          scale: 1.03,
          duration: 0.3,
          ease: 'power2.out'
        });
      });

      button.addEventListener('mouseleave', () => {
        rect = null;
        gsap.to(button, {
          x: 0,
          y: 0,
          scale: 1,
          duration: 0.3,
          ease: 'power2.out'
        });
        gsap.to(bg, {
          y: '100%',
          duration: 0.4,
          ease: 'power3.in'
        });
      });
    });

    return () => ctx.revert();
  }, []);

  const baseStyles = "relative px-8 py-3 rounded-full overflow-hidden font-bold text-lg transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 inline-flex items-center justify-center cursor-pointer";
  
  const variants = {
    primary: "bg-primary text-white border-2 border-primary hover:bg-primary/90",
    accent: "bg-accent text-ink border-2 border-accent hover:bg-accent/90",
    outline: "bg-transparent text-ink border-2 border-ink hover:bg-ink hover:text-white"
  };

  const hoverBgColors = {
    primary: "bg-white/20",
    accent: "bg-white/20",
    outline: "bg-ink"
  };

  return (
    <button 
      ref={buttonRef}
      className={`${baseStyles} ${variants[variant]} ${className}`}
      {...props}
    >
      <span 
        ref={bgRef} 
        className={`absolute inset-0 translate-y-full ${hoverBgColors[variant]} z-0`}
      />
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
    </button>
  );
};
